<template>
  <div style="max-width: 42.75rem">
    <h1 class="subs-title">{{ adminText('organizationAll.membership_request_bbfcdecb', 'Membership Request') }}</h1>
    <div style="display: flex; gap: 1rem; align-items: center">
      <v-text-field
        :label="adminText('organizationAll.search_memberships_80d67212', 'Search memberships')"
        append-icon="search"
        box
        :disabled="!loading && !requests.length"
        style="max-width: 42.75rem; margin: 1rem 0"
        @change="handleSearchChange($event)"
      />
      <v-select
        :label="adminText('organizationAll.sport_memberships_37e3f5d7', 'Sport memberships')"
        :items="sportMemberships"
        item-text="name"
        :disabled="!loading && !requests.length"
        item-value="id"
        box
        @change="handlePlanChange"
      />
    </div>
    <div v-if="!loading && !requests.length" class="no-requests">
      {{ adminText('organizationAll.currently_there_are_no_membership_requests_675a90e1', 'Currently there are no membership requests.') }}
    </div>
    <div v-else>
      <div v-for="request in requests" :key="request.id" class="request-container">
        <div class="request-item">
          <p class="name">{{ getRequestName(request) }}</p>
          <p class="date">
            {{ formatDate(request.createdAt) }} - {{ request.membershipPlan.name }}
          </p>
        </div>
        <div style="display: flex; gap: 1rem">
          <button id="deny-request-button" @click="denyRequest(request)">
            <v-icon color="red"> close</v-icon>
          </button>
          <main-button class="request-button" @click="approveRequest(request)">
            <v-icon>check</v-icon>{{ adminText('organizationAll.confirm_04a21221', 'Confirm') }}
          </main-button>
        </div>
      </div>
    </div>

    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="organizationText(snackbar.message)"
      :color="snackbar.color"
    />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import { GET_SPORT_ORGANIZATION_MEMBERSHIP_PLANS } from '@/graphql/membershipPlan.graphql'
import {
  GET_SPORT_MEMBERSHIP_REQUESTS,
  UPDATE_MEMBERSHIP_REQUEST_STATUS,
} from '@/graphql/membershipUser.graphql'
import moment from 'moment'

export default {
  mixins: [OrganizationLocalization],
  name: 'sport-membership-request',
  components: {
    SimpleSnackbar,
  },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    return {
      sportMemberships: [],
      selectedPlanId: null,
      requests: [],
      searchTerm: '',
      snackbar: {
        open: false,
        message: '',
        color: '',
      },
      loading: true,
      pagination: {
        page: 1,
        per_page: 50,
      },
    }
  },
  async mounted() {
    await this.fetchPlans()
    await this.fetchRequests()
  },
  methods: {
    async fetchPlans() {
      const { data } = await this.$apollo.query({
        query: GET_SPORT_ORGANIZATION_MEMBERSHIP_PLANS,
        variables: { organizationSlug: this.organizationSlug },
      })
      this.sportMemberships = data.getOrganizationMembershipPlans || []
    },
    async fetchRequests() {
      const { data } = await this.$apollo.query({
        query: GET_SPORT_MEMBERSHIP_REQUESTS,
        variables: {
          organizationSlug: this.organizationSlug,
          pagination: this.pagination,
          searchTerm: this.searchTerm || null,
          membershipPlanId: this.selectedPlanId || null,
          status: 'PENDING',
        },
        fetchPolicy: 'network-only',
      })

      const { items } = data.getSportMembershipRequests || { items: [] }
      this.requests = items || []
      this.loading = false
    },
    async handleSearchChange(value) {
      this.searchTerm = value
      await this.fetchRequests()
    },
    async handlePlanChange(value) {
      this.selectedPlanId = value
      await this.fetchRequests()
    },
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY')
    },
    getRequestName(request) {
      const fields = request.fields || {}
      const first = fields['First name']
      const last = fields['Last name']
      if (first || last) {
        return `${first || ''} ${last || ''}`.trim()
      }

      return request.name
    },
    async updateRequestStatus(request, status) {
      await this.$apollo.mutate({
        mutation: UPDATE_MEMBERSHIP_REQUEST_STATUS,
        variables: {
          membershipUserId: request.id,
          organizationSlug: this.organizationSlug,
          status,
        },
      })

      this.snackbar = {
        open: true,
        message: status === 'ACCEPTED' ? this.organizationMessage('organizationAll.request_approved_d03d2d6f', 'Request approved.') : this.organizationMessage('organizationAll.request_denied_10607ec0', 'Request denied.'),
        color: status === 'ACCEPTED' ? 'success' : 'error',
      }

      await this.fetchRequests()
    },
    async approveRequest(request) {
      await this.updateRequestStatus(request, 'ACCEPTED')
    },
    async denyRequest(request) {
      await this.updateRequestStatus(request, 'DENIED')
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 64.875rem;
}
.subs-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
.request-container {
  display: flex;
  margin-bottom: 1rem;
  padding: 16px;
  align-items: center;
  justify-content: space-between;
  align-self: stretch;
  border-radius: 16px;
  border: 1px solid var(--Grey-30, #d9d9d9);
}
.request-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.name {
  margin: 0;
  color: var(--Primary-10, #0277bd);
  font-variant-numeric: lining-nums proportional-nums;

  /* Heading/H5 */
  font-family: Raleway;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%; /* 24px */
  letter-spacing: -0.6px;
}
.date {
  margin: 0;
  color: var(--Grey-10, #666);

  /* Body/Small */
  font-family: 'Source Sans Pro';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
}
#deny-request-button {
  display: flex;
  height: 48px;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  border-radius: 24px;
  background: rgba(251, 235, 237, 0.4);
}
.no-requests {
  margin-top: 1.5rem;
  color: var(--Grey-10, #666);
  font-family: 'Source Sans Pro';
  font-size: 16px;
}
</style>
